import React from 'react';
import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { EditorState } from 'lexical';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { Scrollbar } from 'src/components/general/display/scrollbar';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';

const EditorBottomBar = styled(FlexRow)`
  border-top: 1px solid rgba(152, 169, 188, 0.251);
  height: 32px;
  justify-content: space-between;
  padding: 8px 10px;
`;

const BaseInput = styled(FlexColumn)<{ hasError: boolean }>`
  border: 1px solid;
  border-color: ${({ theme, hasError }) =>
    hasError ? theme.colors.complementaryColor7 : 'rgba(152, 169, 188, 0.25)'};
  border-radius: 4px;
`;

const EditorContainer = styled(BaseInput)`
  margin-bottom: 17px;
  width: 100%;

  ${up('md')} {
    max-width: 448px;
  }
`;

const TextInputContainer = styled.div`
  margin: 10px 12px;
  min-height: 173px;
  position: relative;

  .editor-input {
    max-height: 200px;
    outline: none;
  }

  .editor-paragraph {
    color: ${({ theme }) => theme.colors.smalt};
    font-family: ${({ theme }) => theme.font.regular};
    font-size: 13px;
    font-weight: 400;
    line-height: 20px;
    text-align: left;
  }
`;

const PlaceholderText = styled.span`
  color: ${({ theme }) => theme.colors.geyser};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  pointer-events: none;
  position: absolute;
  text-align: left;
  top: 0;
`;

const PluginsContainer = styled(FlexRow)`
  & > * {
    &:not(:last-child) {
      margin-right: 10px;
    }
  }
`;

const LexicalMessageInputContainer = styled.div`
  position: relative;
  height: 100%;

  .editor-input {
    height: 173px;
  }
`;

type LexicalTextEditorProps = React.ComponentPropsWithoutRef<typeof EditorContainer> & {
  hasError: boolean;
  editorConfig: InitialConfigType;
  placeholderText: string;
  onChange: (editorState: EditorState) => void;
  bottomExtraContent: JSX.Element | null;
  testId: string;
  plugins?: JSX.Element;
};

export const LexicalTextEditor = ({
  hasError,
  editorConfig,
  placeholderText,
  onChange,
  bottomExtraContent,
  testId,
  plugins,
  ...rest
}: LexicalTextEditorProps) => {
  return (
    <EditorContainer hasError={hasError} {...rest}>
      <LexicalComposer initialConfig={editorConfig}>
        <TextInputContainer className="text-input-container">
          <PlainTextPlugin
            contentEditable={
              <LexicalMessageInputContainer data-lgg-id={testId}>
                <Scrollbar>
                  <ContentEditable className="editor-input" />
                </Scrollbar>
              </LexicalMessageInputContainer>
            }
            placeholder={
              <PlaceholderText className="placeholder-text">
                {placeholderText}
              </PlaceholderText>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <OnChangePlugin onChange={onChange} />
        </TextInputContainer>
        <EditorBottomBar>
          <PluginsContainer>{plugins}</PluginsContainer>
          {bottomExtraContent}
        </EditorBottomBar>
      </LexicalComposer>
    </EditorContainer>
  );
};
